import { capitalize } from "lodash-es";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BAND_PATHS } from "Bands/router";
import { clearActionMeta, dataMetaSelector, subscriptionsSelector } from "Bands/state";
import { getSubscriptionsAction } from "Bands/state/data/utils/helpers";
import { BAND_ACTIONS, IBand, IBandSubscription } from "Bands/types";
import { getSubscriptionStatus, useAdminSubscriptionActions } from "Bands/utils";
import { useHistory } from "react-router";
import { copy } from "shared/assets";
import { Band } from "shared/assets/icons";
import { Card, IActionDropdownItem, Table, TableColumn, TableSkeleton } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import { BoldText, FlexContainer, Icon, MutedText, SmallText } from "shared/styles";
import { IAPIState, IStatus } from "shared/types";
import { buildUrl, formatDateTime } from "shared/utils";
const historyCopy = copy.adminDashboard.subscriptionHistory;
const { adminDashboard: dashboardCopy } = copy;

interface Props {
  band: IBand;
}

export const SubscriptionHistory: FC<Props> = ({ band }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const subscriptions = useSelector(subscriptionsSelector);

  const { api: customSubscriptionApiMeta, createdItem } = useSelector(
    dataMetaSelector(BAND_ACTIONS.requestCustomSubscription)
  );

  const { api: apiMeta } = useSelector(
    dataMetaSelector(
      getSubscriptionsAction({
        band: band.id,
        page: 1,
        itemsPerPage: 15,
      })
    )
  );

  const bandSubscriptions = useMemo(
    () => subscriptions.filter(({ bands }) => bands.find(({ id }) => id === band.id)),
    [subscriptions, band.id]
  );

  const { getSubscriptionActions } = useAdminSubscriptionActions();

  const getRowActions = useCallback(
    (id: string): IActionDropdownItem[] => {
      const subscription = bandSubscriptions.find(subscription => subscription.id === id);

      if (!subscription) return [];

      return getSubscriptionActions({ bandId: band.id, subscription });
    },
    [band.id, bandSubscriptions, getSubscriptionActions]
  );

  useEffect(() => {
    if (customSubscriptionApiMeta?.status === IAPIState.succeeded) {
      if (!createdItem?.id) {
        dispatch(
          addTemporaryToast({
            status: IStatus.error,
            message: dashboardCopy.customSubscriptions.actions.createCustomSubscriptionRequestError,
          })
        );
        return;
      }

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: dashboardCopy.customSubscriptions.actions.createCustomSubscriptionRequestSuccess,
        })
      );

      history.push(
        buildUrl(BAND_PATHS.ADMIN_DASHBOARD, {}, "", {
          requestId: createdItem?.id,
        })
      );

      dispatch(clearActionMeta(BAND_ACTIONS.requestCustomSubscription));
    }
  }, [customSubscriptionApiMeta, history, dispatch, createdItem]);

  const renderInner = useMemo(() => {
    if (apiMeta?.status === IAPIState.pending) return <TableSkeleton />;

    return (
      <Table
        data={bandSubscriptions}
        columns={columns}
        emptyCopy={historyCopy.messages.empty}
        getRowActions={getRowActions}
      />
    );
  }, [apiMeta?.status, bandSubscriptions, getRowActions]);

  return (
    <Card title={historyCopy.title} icon={Band}>
      {renderInner}
    </Card>
  );
};

const columns: TableColumn[] = [
  {
    Header: "Name",
    accessor: "plan",
    Cell: ({ row }) => {
      const subscription = row.original as IBandSubscription;

      return (
        <Spacer size="xs">
          <BoldText>{capitalize(subscription.plan)}</BoldText>
          <SmallText as={MutedText}>{subscription.id}</SmallText>
        </Spacer>
      );
    },
  },
  {
    Header: "Status",
    accessor: "status",
    Cell: ({ value }) => {
      const { icon, iconColor } = getSubscriptionStatus(value);

      return (
        <FlexContainer flexWrap="nowrap">
          <Icon as={icon} color={iconColor} />
          {value}
        </FlexContainer>
      );
    },
  },
  {
    Header: "Updated at",
    accessor: "updatedAt",
    Cell: ({ value }) => formatDateTime(value),
  },
  {
    Header: "Period",
    accessor: "start",
    Cell: ({ row }) => {
      const subscription = row.original as IBandSubscription;

      return (
        <>
          {formatDateTime(subscription.start, { dateOnly: true })} -{" "}
          {formatDateTime(subscription.stop, { dateOnly: true })}
        </>
      );
    },
  },
  {
    Header: "Billing status",
    accessor: "billingStatus",
  },
  {
    Header: "Payment method",
    accessor: "paymentMethod",
  },
];
